import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { NotificationsOff, NotificationsNone } from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const formatReminderDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm', { locale: pl });
  } catch {
    return dateStr;
  }
};

const ActiveReminderDialog = ({
  open,
  client,
  onClose,
  onDismiss,
  onAddNew,
  isPending,
  error,
}) => {
  if (!client) return null;

  const isActive = Boolean(client.hasActiveReminder);
  const canDismiss = isActive && Boolean(client.reminderId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        {isActive ? (
          <NotificationsOff color="error" />
        ) : (
          <NotificationsNone color="disabled" />
        )}
        {isActive ? 'Aktywne przypomnienie' : 'Brak aktywnego przypomnienia'}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {client.companyName}
        </Typography>

        {isActive ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'error.50',
              border: 1,
              borderColor: 'error.200',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Zaplanowane na
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatReminderDate(client.remindAt)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Ten klient nie ma obecnie aktywnego przypomnienia.
          </Typography>
        )}

        {isActive && !client.reminderId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Nie udało się pobrać identyfikatora przypomnienia.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={isPending}>
          Zamknij
        </Button>
        {!isActive && onAddNew && (
          <Button
            variant="contained"
            color="error"
            startIcon={<NotificationsOff />}
            onClick={() => {
              onClose();
              onAddNew();
            }}
            disabled={isPending}
          >
            Dodaj przypomnienie
          </Button>
        )}
        {isActive && onAddNew && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              onClose();
              onAddNew();
            }}
            disabled={isPending}
          >
            Dodaj nowe
          </Button>
        )}
        {isActive && (
          <Button
            variant="contained"
            color="error"
            disabled={isPending || !canDismiss}
            startIcon={isPending ? <CircularProgress size={16} /> : <NotificationsOff />}
            onClick={onDismiss}
          >
            Wyłącz przypomnienie
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ActiveReminderDialog;
